import { Checkbox } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import MuiSelect, { SelectChangeEvent } from '@mui/material/Select'
import { useTranslation } from 'next-i18next'
import { ReactNode, useState } from 'react'
import { InputLabel } from 'components/common/TextInput'
import { BlackText } from 'components/common/common.style'

import { unifiedColors } from 'styles/colors'
import {
  MenuItemDisplay,
  SelectSXErrorProps,
  SelectSXProps
} from './Select.style'

interface SelectComponentProps<T> {
  inputLabel?: string
  fullSize?: boolean
  errorMessage?: string
  defaultValue?: T
  placeholder: string
  options: {
    display: string | ReactNode
    value: string | number
    key?: string
    disabled?: boolean
    selected?: boolean
  }[]
  multiple?: boolean
  allSelectedMessage?: string
  bold?: boolean
  style?: object
  noMargin?: boolean
  disabled?: boolean
  maxHeight?: string
  onChange: (event: SelectChangeEvent<T>) => void
  value: T
  inputRef?: React.Ref<HTMLDivElement>
  onBlur?: () => void
  openOnMount?: boolean
}

const SelectComponent = <T,>({
  inputLabel,
  errorMessage,
  placeholder,
  options,
  disabled,
  bold,
  style = {},
  multiple,
  onChange,
  maxHeight,
  value,
  inputRef,
  onBlur,
  allSelectedMessage,
  openOnMount
}: SelectComponentProps<T>) => {
  const [open, setOpen] = useState(openOnMount || false)
  const isAllSelected =
    options.length > 0 && options.every((option) => option.selected)
  const selected = options.filter((option) => option.selected)
  const { t } = useTranslation('common')

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }
  return (
    <>
      {inputLabel && <InputLabel>{inputLabel}</InputLabel>}
      <MuiSelect<T>
        value={value}
        multiple={multiple}
        disabled={disabled}
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        onChange={(e) => {
          if (Array.isArray(e.target.value) && e.target.value.includes('all')) {
            const event = {
              target: {
                value:
                  selected.length > 0
                    ? []
                    : options
                        .filter((option) => !option.disabled)
                        .map((option) => option.value)
              }
            } as SelectChangeEvent<T>
            onChange(event)
            return
          }

          onChange(e)
        }}
        onBlur={onBlur}
        ref={inputRef}
        inputRef={inputRef}
        displayEmpty
        sx={{
          ...SelectSXProps,
          ...(errorMessage && SelectSXErrorProps),
          ...style
        }}
        renderValue={(value) => {
          return Array.isArray(value) ? (
            isAllSelected && allSelectedMessage ? (
              <MenuItemDisplay>
                <BlackText semiBold={bold} ellipsis>
                  {allSelectedMessage}
                </BlackText>
              </MenuItemDisplay>
            ) : value.length > 0 ? (
              <MenuItemDisplay>
                <BlackText semiBold={bold} ellipsis>
                  {value
                    .filter((v: string) => v !== 'all')
                    .map((v: string) =>
                      v === '' || v === undefined
                        ? placeholder
                        : options.find((option) => option.value === v)?.display
                    )
                    .join(', ')}
                </BlackText>
              </MenuItemDisplay>
            ) : (
              <MenuItemDisplay>
                <BlackText semiBold={bold}>{placeholder}</BlackText>
              </MenuItemDisplay>
            )
          ) : (
            <MenuItemDisplay>
              <BlackText semiBold={bold}>
                {value === '' || value === undefined
                  ? placeholder
                  : options.find((option) => option.value === value)?.display}
              </BlackText>
            </MenuItemDisplay>
          )
        }}
        MenuProps={{
          PaperProps: { sx: { maxHeight: maxHeight || '210px' } },
          variant: multiple ? 'menu' : 'selectedMenu'
        }}
      >
        {multiple && (
          <MenuItem
            value="all"
            sx={{
              '&.MuiMenuItem-root, &.Mui-focusVisible': {
                backgroundColor: isAllSelected
                  ? unifiedColors.surface.secondary
                  : unifiedColors.surface.default,
                '&:hover': {
                  backgroundColor: isAllSelected
                    ? unifiedColors.surface.purple50
                    : unifiedColors.surface.secondary
                }
              }
            }}
          >
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                selected.length > 0 && selected.length < options.length
              }
            />
            <MenuItemDisplay>
              {t(selected.length > 0 ? 'unSelectAll' : 'selectAll')}
            </MenuItemDisplay>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            disabled={option.disabled}
            key={option.key ?? option.value}
            value={option.value}
            sx={{
              '&.Mui-selected': {
                backgroundColor: unifiedColors.surface.secondary,
                '&:hover': {
                  backgroundColor: unifiedColors.surface.purple50
                }
              }
            }}
          >
            {multiple && <Checkbox checked={option.selected} />}
            <MenuItemDisplay>{option.display}</MenuItemDisplay>
          </MenuItem>
        ))}
      </MuiSelect>
    </>
  )
}

export default SelectComponent
